import { Plus, Trash2, Users, Truck, Baby, Flame, Receipt, Sparkles } from "lucide-react";

const PRESETS = [
  { label: "Per Person", kind: "per_person", icon: Users },
  { label: "Delivery", kind: "flat", icon: Truck },
  { label: "Kids", kind: "per_person", icon: Baby },
  { label: "Hookah", kind: "flat", icon: Flame },
  { label: "Flat Total", kind: "flat", icon: Receipt },
  { label: "Custom", kind: "flat", icon: Sparkles },
];

function uid() {
  return "li_" + Math.random().toString(36).slice(2, 10);
}

export default function LineItemsEditor({ items = [], guestCount = 0, onChange, testidPrefix = "li" }) {
  function add(preset) {
    onChange([
      ...items,
      { id: uid(), label: preset.label === "Custom" ? "" : preset.label, kind: preset.kind, quantity: 1, unit_price: 0 },
    ]);
  }
  function update(idx, patch) {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  }
  function remove(idx) {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  }

  function lineTotal(it) {
    const up = Number(it.unit_price) || 0;
    if (it.kind === "per_person") return (Number(guestCount) || 0) * up;
    return (Number(it.quantity) || 0) * up;
  }

  const subtotal = items.reduce((a, it) => a + lineTotal(it), 0);

  return (
    <div data-testid={`${testidPrefix}-editor`}>
      {items.length === 0 ? (
        <div className="text-white/40 text-sm py-4 text-center bg-[#0a0a0a] border border-dashed border-white/10">
          No line items yet. Add one below.
        </div>
      ) : (
        <div className="space-y-2">
          {/* Desktop header */}
          <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 px-1 pb-1">
            <div className="col-span-4">Item</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Unit Price</div>
            <div className="col-span-1 text-right">Line</div>
            <div className="col-span-1"></div>
          </div>

          {items.map((it, idx) => {
            const isPP = it.kind === "per_person";
            const total = lineTotal(it);
            return (
              <div key={it.id || idx} className="grid grid-cols-12 gap-2 items-center bg-[#0a0a0a] border border-white/10 p-2" data-testid={`${testidPrefix}-row-${idx}`}>
                <input
                  className="col-span-12 md:col-span-4 bg-transparent border-b border-white/10 px-2 py-1.5 text-sm text-white focus:border-[#C9963F] focus:outline-none"
                  placeholder="Label (e.g. Delivery)"
                  value={it.label}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  data-testid={`${testidPrefix}-label-${idx}`}
                />
                <select
                  className="col-span-6 md:col-span-2 bg-[#050505] border border-white/10 px-2 py-1.5 text-xs text-white/80 focus:border-[#C9963F] focus:outline-none"
                  value={it.kind}
                  onChange={(e) => update(idx, { kind: e.target.value })}
                  data-testid={`${testidPrefix}-kind-${idx}`}
                >
                  <option value="flat">Flat</option>
                  <option value="per_person">Per Person</option>
                </select>
                <div className="col-span-3 md:col-span-2">
                  {isPP ? (
                    <div className="text-right text-xs text-white/40 pr-2 pt-1">× {guestCount || 0}</div>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-sm text-white text-right focus:border-[#C9963F] focus:outline-none"
                      value={it.quantity}
                      onChange={(e) => update(idx, { quantity: e.target.value })}
                      data-testid={`${testidPrefix}-qty-${idx}`}
                    />
                  )}
                </div>
                <div className="col-span-3 md:col-span-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-sm text-white text-right focus:border-[#C9963F] focus:outline-none"
                    value={it.unit_price}
                    onChange={(e) => update(idx, { unit_price: e.target.value })}
                    data-testid={`${testidPrefix}-price-${idx}`}
                  />
                </div>
                <div className="col-span-9 md:col-span-1 text-right text-sm text-[#C9963F] font-medium">
                  ${total.toFixed(2)}
                </div>
                <div className="col-span-3 md:col-span-1 flex justify-end">
                  <button onClick={() => remove(idx)} type="button" data-testid={`${testidPrefix}-remove-${idx}`}
                    className="p-2 text-white/40 hover:text-red-400" aria-label="Remove item">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add presets */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Quick add</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => add(p)}
              data-testid={`${testidPrefix}-add-${p.label.replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F] transition-colors"
            >
              <p.icon size={12} /> {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-baseline justify-between text-sm border-t border-white/10 pt-3">
        <span className="text-white/50 uppercase tracking-wider text-xs">Items Subtotal</span>
        <span className="text-[#C9963F] font-medium" data-testid={`${testidPrefix}-subtotal`}>${subtotal.toFixed(2)}</span>
      </div>
    </div>
  );
}
